import { useMerchant, useSmartWallet } from "@/contexts";
import useContractWrite from "./useContractWrite";
import { ethers } from "ethers";
import { merchantRegistryContract, usdtContract } from "@/contexts/SmartWallet";
import { customPrepareContractCall } from "@/helper/thirdweb";

const useMerchantWrite = () => {
  const { smartWalletAddress, account } = useSmartWallet();

  const {
    merchantConfig,
    merchantDetails,
    isLoading: merchantLoading,
    hasFetched: merchantFetched,
  } = useMerchant();

  const {
    mutateAsync: allowanceMutateAsync,
    isLoading: allowanceIsLoading,
    error: allowanceError,
  } = useContractWrite();

  const {
    mutateAsync: registerMutateAsync,
    isLoading: registerIsLoading,
    error: registerError,
  } = useContractWrite();

  const {
    mutateAsync: stakeMutateAsync,
    isLoading: stakeIsLoading,
    error: stakeError,
  } = useContractWrite();

  const {
    mutateAsync: requestUnstakeMutateAsync,
    isLoading: requestUnstakeIsLoading,
    error: requestUnstakeError,
  } = useContractWrite();

  const {
    mutateAsync: toggleOnlineOfflineMutateAsync,
    isLoading: toggleOnlineOfflineIsLoading,
    error: toggleOnlineOfflineError,
  } = useContractWrite();

  const {
    mutateAsync: updateUpiMutateAsync,
    isLoading: updateUpiIsLoading,
    error: updateUpiError,
  } = useContractWrite();

  const {
    mutateAsync: withdrawEarningsMutateAsync,
    isLoading: withdrawEarningsIsLoading,
    error: withdrawEarningsError,
  } = useContractWrite();

  const registerByStaking = async (stakeAmount: string) => {
    if (!merchantDetails?.isRegistered) {
      if (
        !registerError &&
        !registerIsLoading &&
        !merchantLoading &&
        merchantFetched &&
        merchantConfig
      ) {
        try {
          const approveTransaction = await customPrepareContractCall({
            contract: usdtContract,
            method: "approve",
            params: [
              import.meta.env.VITE_MERCHANT_REGISTRY,
              ethers.utils.parseUnits(stakeAmount, 6),
            ],
            address: account?.address,
          });
          const allowanceResult = await allowanceMutateAsync([
            approveTransaction,
          ]);

          if (!allowanceIsLoading && !allowanceError) {
            const registerTransaction = await customPrepareContractCall({
              contract: merchantRegistryContract,
              method: "register",
              params: [ethers.utils.parseUnits(stakeAmount, 6)],
              address: account?.address,
            });
            const result = await registerMutateAsync([registerTransaction]);
            console.log("registerByStaking -- result -->", result);
            return result;
          } else {
            console.error(
              "allowanceIsLoading and allowanceError and allowanceResult",
              allowanceIsLoading,
              allowanceError,
              allowanceResult,
            );
            throw allowanceError;
          }
        } catch (error) {
          console.error("handleRegister -- catch -- error -->", error);
          return error;
        }
      } else {
        console.error(
          "!registerError && !registerIsLoading && !merchantIsLoading && merchantFetched && merchantConfig",
          !registerError,
          !registerIsLoading,
          !merchantLoading,
          merchantFetched,
          merchantConfig,
        );
      }
    } else {
      console.log(
        `Merchant ${smartWalletAddress} already registered => ${merchantDetails}`,
      );

      return registerError;
    }
  };

  const stake = async (stakeAmount: string) => {
    if (!stakeError && !stakeIsLoading) {
      try {
        const approveTransaction = await customPrepareContractCall({
          contract: usdtContract,
          method: "approve",
          params: [
            import.meta.env.VITE_MERCHANT_REGISTRY,
            ethers.utils.parseUnits(stakeAmount, 6),
          ],
          address: account?.address,
        });
        const allowanceResult = await allowanceMutateAsync([
          approveTransaction,
        ]);

        if (!allowanceIsLoading && !allowanceError) {
          const stakeTransaction = await customPrepareContractCall({
            contract: merchantRegistryContract,
            method: "stake",
            params: [ethers.utils.parseUnits(stakeAmount, 6)],
            address: account?.address,
          });
          const result = await stakeMutateAsync([stakeTransaction]);
          console.log("stake -- result -->", result);
          return result;
        } else {
          console.error(
            "allowanceIsLoading and allowanceError and allowanceResult",
            allowanceIsLoading,
            allowanceError,
            allowanceResult,
          );
          throw allowanceError;
        }
      } catch (error) {
        console.error("stake -- catch -- error -->", error);
        return error;
      }
    } else {
      console.log(
        "!stakeError && !stakeIsLoading",
        !stakeError,
        !stakeIsLoading,
      );

      return stakeError;
    }
  };

  const requestUnstake = async () => {
    if (!requestUnstakeError && !requestUnstakeIsLoading) {
      const requestUnstakeTransaction = await customPrepareContractCall({
        contract: merchantRegistryContract,
        method: "requestUnstake",
        address: account?.address,
      });
      try {
        const result = await requestUnstakeMutateAsync([
          requestUnstakeTransaction,
        ]);
        console.log("requestUnstake -- result -->", result);
        return result;
      } catch (error) {
        console.error("requestUnstake -- catch -- error -->", error);
        return error;
      }
    } else {
      console.log(
        "!requestUnstakeError && !requestUnstakeIsLoading",
        !requestUnstakeError,
        !requestUnstakeIsLoading,
      );
      console.log("requestUnstakeError ", requestUnstakeError);
      return requestUnstakeError;
    }
  };

  const toggleOnlineOffline = async () => {
    if (
      merchantConfig &&
      merchantDetails &&
      merchantDetails?.stake >= merchantConfig?.minStake
    ) {
      if (!toggleOnlineOfflineError && !toggleOnlineOfflineIsLoading) {
        try {
          const toggleTransaction = await customPrepareContractCall({
            contract: merchantRegistryContract,
            method: "toggleOnlineOffline",
            params: [smartWalletAddress],
            address: account?.address,
          });
          const result = await toggleOnlineOfflineMutateAsync([
            toggleTransaction,
          ]);
          console.log("toggleOnlineOffline -- result -->", result);
          return result;
        } catch (error) {
          console.error("toggleOnlineOffline -- catch -- error -->", error);
          return error;
        }
      } else {
        console.log(
          "!toggleOnlineOfflineError && !toggleOnlineOfflineIsLoading",
          !toggleOnlineOfflineError,
          !toggleOnlineOfflineIsLoading,
        );
      }
    } else {
      console.error(
        "toggleOnlineOffline -- Merchant not active : stake is not greater than or equal to minStake",
      );

      return toggleOnlineOfflineError;
    }
  };

  const updateUpi = async (upiId: string) => {
    if (!updateUpiError && !updateUpiIsLoading) {
      try {
        const updateUpiIdTransaction = await customPrepareContractCall({
          contract: merchantRegistryContract,
          method: "updateUpi",
          params: [upiId],
          address: account?.address,
        });

        const result = await updateUpiMutateAsync([updateUpiIdTransaction]);

        return result;
      } catch (error) {
        console.error("updateUpi -- catch -- error -->", error);
        return error;
      }
    } else {
      console.log("upiUpdateError", updateUpiError);

      return updateUpiError;
    }
  };
  const withdrawEarnings = async () => {
    if (!withdrawEarningsError && !withdrawEarningsIsLoading) {
      try {
        const withdrawEarningsTransaction = await customPrepareContractCall({
          contract: merchantRegistryContract,
          method: "withdrawFee",
          params: [],
          address: account?.address,
        });

        const result = await withdrawEarningsMutateAsync([
          withdrawEarningsTransaction,
        ]);

        return result;
      } catch (error) {
        console.error("withdrawEarnings -- catch -- error -->", error);
        return error;
      }
    } else {
      console.log("withdrawEarningsError", withdrawEarningsError);

      return withdrawEarningsError;
    }
  };

  return {
    registerByStaking,
    stake,
    requestUnstake,
    toggleOnlineOffline,
    updateUpi,
    withdrawEarnings,
  };
};

export default useMerchantWrite;
